export interface GetI18nEnumLabelOptions {
	/** e.g. `properties.source` */
	i18nKeyPrefix: string;
	/** e.g. Transloco `translate` function. If omitted, falls back to `humanizeEnumKey`. */
	translate?: (key: string) => string;
	/** Optional custom fallback. Defaults to `humanizeEnumKey`. */
	fallback?: (raw: string) => string;
}

export interface HumanizeEnumKeyOptions {
	/**
	 * If true, keeps short tokens (<=2) in upper-case (e.g. "AI", "ID").
	 * Defaults to true.
	 */
	keepShortTokensUppercase?: boolean;
}

/**
 * Converts values like `NOTE_FROM_WORD_DOCX` into `Note From Word Docx`.
 * Useful as a safe fallback when no i18n translation exists.
 */
export function humanizeEnumKey(value: unknown, options: HumanizeEnumKeyOptions = {}): string {
	const { keepShortTokensUppercase = true } = options;
	// eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
	const trimmed = `${value ?? ''}`.trim();
	if (!trimmed) {
		return '';
	}

	return trimmed
		.split('_')
		.filter(Boolean)
		.map((part) => {
			const lower = part.toLowerCase();
			if (keepShortTokensUppercase && lower.length <= 2) {
				return lower.toUpperCase();
			}
			return lower.charAt(0).toUpperCase() + lower.slice(1);
		})
		.join(' ');
}

/**
 * Resolves a label for an enum-like value using i18n.
 *
 * Example:
 * - value: `PLUGIN`
 * - i18nKeyPrefix: `properties.source`
 * - translate(`properties.source.PLUGIN`) => `Browser Extension`
 */
export function getI18nEnumLabel(value: unknown, options: GetI18nEnumLabelOptions): string {
	// eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
	const raw = `${value ?? ''}`;
	const fallback = options.fallback ?? ((v: string) => humanizeEnumKey(v));
	const key = `${options.i18nKeyPrefix}.${raw}`;
	const translated = options.translate?.(key);
	if (translated && translated !== key) {
		return translated;
	}
	return fallback(raw);
}
